/**
 * PairSettingsPage — S16 pair settings (Phase J of the design-takeover).
 *
 * Route: /pair/:pairId/settings
 *
 * Three sections (authoritative from copy-decisions doc Phase J / S16):
 *   1. Session cadence — change cadence with a Save button
 *   2. Pause sessions — pause / resume the pair
 *   3. End this pair  — permanently end the relationship
 *
 * Pause/Resume and End each use a ConfirmDialog with exact copy from the
 * copy-decisions doc. End uses tone="danger".
 *
 * The "paused" banner ("Sessions are paused for this pair.") appears at the
 * top of the page when the pair is paused.
 *
 * Backend wiring:
 *   - updatePairCadence — existing Phase C mutation
 *   - pausePair / resumePair / endPair — Phase C + Phase J mutations
 */
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { appsyncQuery } from '../api/appsync-client.js'
import { Button, ConfirmDialog } from '../ui'

// ─── GraphQL ────────────────────────────────────────────────────────────────

const GET_PAIR = `
  query GetPair($pairId: ID!) {
    getPair(pairId: $pairId) {
      pairId
      reportId
      managerId
      status
      cadence
      reportName
      reportEmail
      managerName
      managerEmail
    }
  }
`

const UPDATE_PAIR_CADENCE = `
  mutation UpdatePairCadence($pairId: ID!, $cadence: String!) {
    updatePairCadence(pairId: $pairId, cadence: $cadence) {
      pairId
      status
      cadence
    }
  }
`

const PAUSE_PAIR = `
  mutation PausePair($pairId: ID!) {
    pausePair(pairId: $pairId) {
      pairId
      status
      cadence
    }
  }
`

const RESUME_PAIR = `
  mutation ResumePair($pairId: ID!) {
    resumePair(pairId: $pairId) {
      pairId
      status
      cadence
    }
  }
`

const END_PAIR = `
  mutation EndPair($pairId: ID!) {
    endPair(pairId: $pairId) {
      pairId
      status
      endedAt
    }
  }
`

// ─── Cadence options ────────────────────────────────────────────────────────

const CADENCE_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
]

// ─── PairSettingsPage ────────────────────────────────────────────────────────

export function PairSettingsPage() {
  const { pairId } = useParams()
  const navigate = useNavigate()

  const [pair, setPair] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  // Cadence state
  const [cadence, setCadence] = useState('weekly')
  const [cadenceSaving, setCadenceSaving] = useState(false)
  const [cadenceError, setCadenceError] = useState(null)
  const [cadenceSaved, setCadenceSaved] = useState(false)

  // Pause state
  const [showPauseConfirm, setShowPauseConfirm] = useState(false)
  const [pauseLoading, setPauseLoading] = useState(false)

  // Resume state
  const [showResumeConfirm, setShowResumeConfirm] = useState(false)
  const [resumeLoading, setResumeLoading] = useState(false)

  // End state
  const [showEndConfirm, setShowEndConfirm] = useState(false)
  const [endLoading, setEndLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    appsyncQuery(GET_PAIR, { pairId })
      .then((data) => {
        if (cancelled) return
        const p = data?.getPair ?? null
        setPair(p)
        if (p?.cadence) setCadence(p.cadence)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(err?.message || 'Failed to load pair settings.')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [pairId])

  // Derive the other person's name for confirmation dialogs
  const partnerName =
    pair?.reportName || pair?.reportEmail || pair?.managerName || pair?.managerEmail || 'your pair'

  const isPaused = pair?.status === 'paused'
  const isEnded = pair?.status === 'ended'

  async function handleSaveCadence() {
    setCadenceSaving(true)
    setCadenceError(null)
    setCadenceSaved(false)
    try {
      await appsyncQuery(UPDATE_PAIR_CADENCE, { pairId, cadence })
      setCadenceSaved(true)
      setTimeout(() => setCadenceSaved(false), 3000)
    } catch (err) {
      setCadenceError(err?.message || 'Failed to save cadence.')
    } finally {
      setCadenceSaving(false)
    }
  }

  async function handlePause() {
    setPauseLoading(true)
    try {
      const data = await appsyncQuery(PAUSE_PAIR, { pairId })
      setPair((prev) => ({ ...prev, ...data?.pausePair }))
      setShowPauseConfirm(false)
    } catch {
      // Leave dialog open so user sees the failure
    } finally {
      setPauseLoading(false)
    }
  }

  async function handleResume() {
    setResumeLoading(true)
    try {
      const data = await appsyncQuery(RESUME_PAIR, { pairId })
      setPair((prev) => ({ ...prev, ...data?.resumePair }))
      setShowResumeConfirm(false)
    } catch {
      // Leave dialog open
    } finally {
      setResumeLoading(false)
    }
  }

  async function handleEnd() {
    setEndLoading(true)
    try {
      const data = await appsyncQuery(END_PAIR, { pairId })
      setPair((prev) => ({ ...prev, ...data?.endPair }))
      setShowEndConfirm(false)
      // Navigate away — the pair is ended, settings are no longer actionable
      navigate('/home')
    } catch {
      // Leave dialog open
    } finally {
      setEndLoading(false)
    }
  }

  // ── Render states ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="pair-settings__loading" aria-busy="true" aria-live="polite">
        Loading pair settings…
      </div>
    )
  }

  if (loadError || !pair) {
    return (
      <div className="pair-settings__error" role="alert">
        {loadError || 'Pair not found.'}
      </div>
    )
  }

  if (isEnded) {
    return (
      <div className="pair-settings">
        <div className="pair-settings__inner">
          <h1 className="pair-settings__heading">Pair settings</h1>
          <p className="pair-settings__ended-notice">
            This pair has ended. No further changes can be made.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="pair-settings">
      <div className="pair-settings__inner">
        <h1 className="pair-settings__heading">Pair settings</h1>
        <p className="pair-settings__subheading">Settings for {partnerName}</p>

        {/* Paused banner */}
        {isPaused && (
          <div
            className="pair-settings__paused-banner"
            role="status"
            aria-live="polite"
          >
            Sessions are paused for this pair.
          </div>
        )}

        {/* ── Cadence section ───────────────────────────────────── */}
        <section className="pair-settings__section" aria-labelledby="cadence-heading">
          <h2 id="cadence-heading" className="pair-settings__section-heading">
            Session cadence
          </h2>
          <p className="pair-settings__helper">
            How often you meet with this person. Overrides your default cadence for this pair.
          </p>
          <div className="pair-settings__card">
            <div className="pair-settings__cadence-row">
              <label htmlFor="pair-cadence" className="pair-settings__field-label">
                Cadence
              </label>
              <select
                id="pair-cadence"
                value={cadence}
                onChange={(e) => {
                  setCadence(e.target.value)
                  setCadenceSaved(false)
                }}
                className="pair-settings__select"
                disabled={cadenceSaving || isPaused}
              >
                {CADENCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {cadenceError && (
              <p className="pair-settings__error" role="alert">
                {cadenceError}
              </p>
            )}
            <div className="pair-settings__card-footer">
              <Button
                size="sm"
                onClick={handleSaveCadence}
                loading={cadenceSaving}
                loadingLabel="Saving…"
                disabled={cadenceSaving || isPaused}
              >
                {cadenceSaved ? 'Saved' : 'Save cadence'}
              </Button>
            </div>
          </div>
        </section>

        {/* ── Pause / Resume section ────────────────────────────── */}
        <section className="pair-settings__section" aria-labelledby="pause-heading">
          <h2 id="pause-heading" className="pair-settings__section-heading">
            {isPaused ? 'Resume sessions' : 'Pause sessions'}
          </h2>
          <p className="pair-settings__helper">
            {isPaused
              ? 'This pair is currently paused. Resume to start creating new sessions again.'
              : 'Pausing stops new sessions from being created for this pair. You can resume at any time.'}
          </p>
          <div className="pair-settings__card">
            <div className="pair-settings__card-footer">
              {isPaused ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowResumeConfirm(true)}
                >
                  Resume pair
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowPauseConfirm(true)}
                >
                  Pause pair
                </Button>
              )}
            </div>
          </div>
        </section>

        {/* ── End section ───────────────────────────────────────── */}
        <section className="pair-settings__section" aria-labelledby="end-heading">
          <h2 id="end-heading" className="pair-settings__section-heading">
            End this pair
          </h2>
          <p className="pair-settings__helper">
            Ending a pair closes the relationship in Soundcheck. This is for when you no longer
            manage this person or no longer share 1:1s.
          </p>
          <div className="pair-settings__card pair-settings__card--danger">
            <div className="pair-settings__card-footer">
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowEndConfirm(true)}
              >
                End pair
              </Button>
            </div>
          </div>
        </section>
      </div>

      {/* ── Pause confirmation ─────────────────────────────────── */}
      <ConfirmDialog
        open={showPauseConfirm}
        title={`Pause sessions with ${partnerName}?`}
        message="No new sessions will be created while the pair is paused. Existing sessions are not affected. You can resume this pair at any time."
        confirmLabel={pauseLoading ? 'Pausing…' : 'Pause'}
        cancelLabel="Cancel"
        onConfirm={handlePause}
        onCancel={() => setShowPauseConfirm(false)}
      />

      {/* ── Resume confirmation ────────────────────────────────── */}
      <ConfirmDialog
        open={showResumeConfirm}
        title={`Resume sessions with ${partnerName}?`}
        message="New sessions will start being created again at your usual cadence."
        confirmLabel={resumeLoading ? 'Resuming…' : 'Resume'}
        cancelLabel="Cancel"
        onConfirm={handleResume}
        onCancel={() => setShowResumeConfirm(false)}
      />

      {/* ── End confirmation ───────────────────────────────────── */}
      <ConfirmDialog
        open={showEndConfirm}
        title={`End pair with ${partnerName}?`}
        message="This closes your 1:1 relationship in Soundcheck. Your session history is preserved and you can still view past sessions, but no new sessions can be created. This cannot be undone."
        confirmLabel={endLoading ? 'Ending…' : 'End pair'}
        cancelLabel="Cancel"
        onConfirm={handleEnd}
        onCancel={() => setShowEndConfirm(false)}
        tone="danger"
      />
    </div>
  )
}
